// 시트 칸 하나의 편집 시작·확정·지우기와 상태줄 합계(순수 함수).

import type { VatMode } from "../../../money/vat";
import { evalQtyExpr, isExpr } from "../../qty-expr";
import { MONEY_MAX, QTY_MAX } from "../../schema";
import { parseMoney, parseNumber } from "../paste";
import {
  cellText,
  dataRowsIn,
  isCellEditable,
  parseSheetCell,
  SheetColumn,
  SheetField,
  SheetRange,
  SheetRow,
} from "./grid-model";
import {
  CellPatch,
  EditorAction,
  EditorItem,
  EditorItemPatch,
} from "../types";

export interface CellEditContext {
  area_pyeong: number | null;
  /** 수량식의 `m²` — 면적 원본. 없으면 평×3.305785. */
  area_m2?: number | null;
  spaces?: { code: string; label: string }[];
  vatMode?: VatMode;
}

/** F2·더블클릭으로 편집을 열 때 입력칸에 넣을 글자. 수량은 식 원문을 보인다. */
export const editTextOf = (
  row: SheetRow,
  column: SheetColumn,
  ctx: CellEditContext
): string => cellText(row, column, { spaces: ctx.spaces, exprs: true, vatMode: ctx.vatMode });

export type CommitResult =
  | { ok: true; patch: EditorItemPatch }
  | { ok: false; message: string };

const failMessage = (field: SheetField, text: string, ctx: CellEditContext): string => {
  if (field === "space_code") return `「${text}」 공간이 없습니다 — 목록에서 고르세요`;
  if (field === "qty") {
    if (isExpr(text)) {
      const result = evalQtyExpr(text, ctx);
      if (!result.ok) return result.message;
    }
    const qty = parseNumber(text);
    if (qty !== null && qty > QTY_MAX) return "수량이 너무 큽니다 — 자릿수를 확인해 주세요";
    return "수량을 읽을 수 없습니다";
  }
  const money = parseMoney(text);
  if (money !== null && money > MONEY_MAX) return "금액이 너무 큽니다 — 자릿수를 확인해 주세요";
  if (parseNumber(text) !== null && money === null) return "금액은 정수로 적어 주세요";
  return "숫자를 읽을 수 없습니다";
};

/** 입력칸 글자를 칸 값으로. 평수가 없어 못 푼 수량식은 식만 남기고 수량을 0 으로 둔다. */
export const commitCell = (
  item: Pick<EditorItem, "labor_unit_price" | "unit">,
  field: SheetField,
  raw: string,
  ctx: CellEditContext
): CommitResult => {
  const parsed = parseSheetCell(field, raw, { qtyExpr: true, ...ctx }, item);
  if (parsed.ok) return parsed;
  const text = raw.trim();
  if (field === "qty" && isExpr(text)) {
    const result = evalQtyExpr(text, ctx);
    if (!result.ok && result.kind === "no_area") {
      return { ok: true, patch: { qty: 0, qty_expr: text } };
    }
  }
  return { ok: false, message: failMessage(field, text, ctx) };
};

// 카드 수량칸은 키마다 확정하므로 단위는 칸에 들어올 때의 단위(baseUnit) 기준으로 채우고, 그게 비었으면 매번 같이 써서 「3E」 가 남지 않게 한다.
export const commitCardQty = (
  item: Pick<EditorItem, "labor_unit_price">,
  raw: string,
  ctx: CellEditContext,
  baseUnit: string
): CommitResult => {
  const result = commitCell({ ...item, unit: baseUnit }, "qty", raw, ctx);
  if (!result.ok || baseUnit.trim() !== "") return result;
  return { ok: true, patch: { unit: "", ...result.patch } };
};

// 마진 칸 글자(「25」·「25%」·「-10」) → 비율 0.25 · −100% 이하는 판매가가 0 아래라 못 받는다.
export const commitMarginText = (raw: string): { ok: true; margin: number } | { ok: false; message: string } => {
  const text = raw.replace(/[%\s]/g, "");
  const pct = text === "" ? NaN : Number(text);
  if (!Number.isFinite(pct)) return { ok: false, message: "마진은 숫자(%)로 적어 주세요" };
  if (pct <= -100) return { ok: false, message: "마진은 −100% 보다 커야 합니다" };
  return { ok: true, margin: pct / 100 };
};

// 카드 단가칸 — 시트 금액칸과 같은 읽기(정수·쉼표·「원」)와 상한(MONEY_MAX)을 쓴다.
export const commitCardMoney = (
  raw: string
): { ok: true; value: number } | { ok: false; message: string } => {
  const text = raw.trim();
  const value = parseMoney(text);
  if (value !== null && value <= MONEY_MAX) return { ok: true, value };
  return { ok: false, message: failMessage("material_unit_price", text, { area_pyeong: null }) };
};

/** 범위의 고칠 수 있는 칸을 비우는 액션(되돌리기 한 단계). 비울 칸이 없으면 null. */
export const clearCellsAction = (
  rows: SheetRow[],
  columns: SheetColumn[],
  range: SheetRange
): Extract<EditorAction, { type: "SET_CELLS" }> | null => {
  const cols = columns.slice(range.left, range.right + 1);
  const patches: CellPatch[] = [];
  for (const row of dataRowsIn(rows, range)) {
    let patch: EditorItemPatch = {};
    for (const column of cols) {
      if (column.field === null || !isCellEditable(row, column)) continue;
      const parsed = parseSheetCell(column.field, "", { qtyExpr: true, area_pyeong: null }, row.item);
      if (parsed.ok) patch = { ...patch, ...parsed.patch };
    }
    if (Object.keys(patch).length > 0) patches.push({ ref: row.ref, patch });
  }
  return patches.length > 0 ? { type: "SET_CELLS", patches } : null;
};

export type QtyDisplay =
  | { kind: "plain" }
  | { kind: "expr"; value: number }
  | { kind: "error"; message: string };

/** 수량 칸의 표시 — 식이면 계산값, 못 풀면 오류 문구. */
export const qtyDisplayOf = (
  item: Pick<EditorItem, "qty" | "qty_expr">,
  area_pyeong: number | null,
  area_m2?: number | null
): QtyDisplay => {
  if (!item.qty_expr) {
    return item.qty > QTY_MAX
      ? { kind: "error", message: "수량이 너무 큽니다 — 자릿수를 확인해 주세요" }
      : { kind: "plain" };
  }
  const result = evalQtyExpr(item.qty_expr, { area_pyeong, area_m2 });
  return result.ok
    ? { kind: "expr", value: result.value }
    : { kind: "error", message: result.message };
};

export type CardQtyNote =
  | { kind: "none" }
  | { kind: "expr"; expr: string; waiting: boolean }
  | { kind: "error"; expr: string; message: string };

/** 카드 보기 수량칸 아래 작은 글 — 식이면 원문, 평수가 없으면 계산 대기, 못 풀면 오류 문구. */
export const cardQtyNoteOf = (
  item: Pick<EditorItem, "qty_expr">,
  area_pyeong: number | null,
  area_m2?: number | null
): CardQtyNote => {
  const expr = (item.qty_expr ?? "").trim();
  if (expr === "") return { kind: "none" };
  const result = evalQtyExpr(expr, { area_pyeong, area_m2 });
  if (result.ok) return { kind: "expr", expr, waiting: false };
  return result.kind === "no_area"
    ? { kind: "expr", expr, waiting: true }
    : { kind: "error", expr, message: result.message };
};

export interface SelectionStats {
  rows: number;
  cols: number;
  /** 숫자 칸 합. 숫자 칸이 없으면 null. */
  sum: number | null;
}

/** 상태줄 — 선택한 항목 행 수 × 열 수와 숫자 칸 합. */
export const selectionStatsOf = (
  rows: SheetRow[],
  columns: SheetColumn[],
  range: SheetRange
): SelectionStats => {
  const dataRows = dataRowsIn(rows, range);
  const cols = columns.slice(range.left, range.right + 1);
  let sum: number | null = null;
  for (const row of dataRows) {
    for (const column of cols) {
      // 마진(%)은 더해 봐야 뜻이 없다.
      if (!column.numeric || column.key === "margin") continue;
      const text = cellText(row, column);
      if (text === "") continue;
      const value = Number(text);
      if (Number.isFinite(value)) sum = (sum ?? 0) + value;
    }
  }
  return { rows: dataRows.length, cols: cols.length, sum };
};

/** 평수 입력칸 글자를 값으로. 비었거나 0 이하·못 읽으면 null. */
export const areaFromText = (raw: string): number | null => {
  if (raw.trim() === "") return null;
  const value = parseNumber(raw);
  return value !== null && value > 0 ? value : null;
};

// 아무것도 안 적힌 줄 — Enter 로 빈 줄을 줄줄이 만들지 않으려고 본다(reducer 의 isBlank 와 같은 조건).
export const isBlankRow = (item: EditorItem): boolean =>
  item.item.trim() === "" &&
  item.scope.trim() === "" &&
  item.unit.trim() === "" &&
  item.space_code.trim() === "" &&
  item.note.trim() === "" &&
  item.qty === 0 &&
  item.material_unit_price === 0 &&
  item.labor_unit_price === 0 &&
  item.children.length === 0;
